import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2, Phone } from "lucide-react";
import { PageHero } from "@/components/layout/PageHero";
import { Reveal } from "@/components/Reveal";
import { useSite } from "@/context/SiteContext";
import { scrollToId } from "@/lib/icons";

const ServicesPage = () => {
  const { content } = useSite();
  const services = content?.services || [];

  useEffect(() => {
    if (window.location.hash && services.length) {
      const id = window.location.hash.slice(1);
      setTimeout(() => scrollToId(id), 500);
    }
  }, [services.length]);

  return (
    <div data-testid="services-page">
      <PageHero
        eyebrow="Our Services"
        title="Nine Disciplines. One Standard of Excellence."
        description="A complete range of professional services designed to meet the diverse needs of businesses, institutions, property owners, and individuals."
      />

      <section className="bg-white py-24">
        <div className="mx-auto max-w-7xl space-y-24 px-6 lg:px-10">
          {services.length === 0 &&
            Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="grid gap-10 lg:grid-cols-2">
                <div className="h-80 animate-pulse rounded-2xl bg-slate-200" />
                <div className="space-y-4 pt-6">
                  <div className="h-8 w-2/3 animate-pulse rounded bg-slate-200" />
                  <div className="h-4 w-full animate-pulse rounded bg-slate-200" />
                  <div className="h-4 w-5/6 animate-pulse rounded bg-slate-200" />
                </div>
              </div>
            ))}

          {services.map((s, i) => (
            <div
              key={s.slug}
              id={s.slug}
              className="grid scroll-mt-28 items-center gap-12 lg:grid-cols-2"
              data-testid={`service-section-${s.slug}`}
            >
              <Reveal className={i % 2 === 1 ? "lg:order-2" : ""}>
                <div className="gold-frame">
                  <img
                    src={s.image}
                    alt={`${s.title} by SSS GROUPS`}
                    className="h-72 w-full rounded-2xl object-cover shadow-2xl sm:h-80"
                    loading="lazy"
                  />
                </div>
              </Reveal>
              <Reveal delay={0.1} className={i % 2 === 1 ? "lg:order-1" : ""}>
                <p className="mb-3 font-mono text-xs font-semibold uppercase tracking-[0.25em] text-gold-dark">
                  Service {String(i + 1).padStart(2, "0")}
                </p>
                <h2 className="font-serif text-3xl font-bold tracking-tight text-navy-900 sm:text-4xl">{s.title}</h2>
                <p className="mt-5 leading-relaxed text-slate-600">{s.short}</p>
                <p className="mt-3 leading-relaxed text-slate-600">{s.overview}</p>
                {s.offerings?.length > 0 && (
                  <ul className="mt-6 space-y-3">
                    {s.offerings.map((o) => (
                      <li key={o} className="flex items-start gap-3 text-sm text-slate-700">
                        <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-gold-dark" />
                        {o}
                      </li>
                    ))}
                  </ul>
                )}
                <div className="mt-8 flex flex-wrap gap-4">
                  <Link
                    to={`/contact?service=${s.slug}`}
                    data-testid={`service-quote-btn-${s.slug}`}
                    className="group flex items-center gap-2 rounded-full bg-gold px-6 py-3 text-sm font-bold uppercase tracking-wider text-navy-950 transition-all duration-300 hover:bg-gold-light"
                  >
                    Request a Quote
                    <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                  </Link>
                  <a
                    href={`tel:${(content?.phone || "").replace(/\s/g, "")}`}
                    className="flex items-center gap-2 rounded-full border-2 border-navy-900 px-6 py-3 text-sm font-bold uppercase tracking-wider text-navy-900 transition-all duration-300 hover:bg-navy-900 hover:text-gold"
                    data-testid={`service-call-btn-${s.slug}`}
                  >
                    <Phone className="h-4 w-4" /> Call Us
                  </a>
                </div>
              </Reveal>
            </div>
          ))}
        </div>
      </section>

      <section className="grain-overlay relative bg-navy-950 py-20">
        <Reveal className="mx-auto max-w-3xl px-6 text-center">
          <h2 className="font-serif text-3xl font-bold text-white sm:text-4xl">
            Not Sure Which Service <span className="gold-text-gradient italic">Fits Your Need?</span>
          </h2>
          <p className="mt-4 text-slate-300">Talk to our team — we will help you identify the right combination of services.</p>
          <Link
            to="/contact"
            data-testid="services-contact-btn"
            className="group mt-8 inline-flex items-center gap-2 rounded-full bg-gold px-8 py-3.5 text-sm font-bold uppercase tracking-wider text-navy-950 transition-all duration-300 hover:bg-gold-light"
          >
            Contact Our Team
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </Reveal>
      </section>
    </div>
  );
};

export default ServicesPage;
